import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach access token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Refresh token on 401
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
          localStorage.setItem('accessToken', data.data.accessToken);
          localStorage.setItem('refreshToken', data.data.refreshToken);
          original.headers.Authorization = `Bearer ${data.data.accessToken}`;
          return api(original);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  adminLogin: (data) => api.post('/auth/admin-login', data),
  logout: (data) => api.post('/auth/logout', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Content
export const categoriesAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getOne: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

export const lessonsAPI = {
  getAll: (params) => api.get('/lessons', { params }),
  getOne: (id) => api.get(`/lessons/${id}`),
  create: (data) => api.post('/lessons', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/lessons/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/lessons/${id}`),
};

export const tipsAPI = {
  getAll: (params) => api.get('/tips', { params }),
  create: (data) => api.post('/tips', data),
  update: (id, data) => api.put(`/tips/${id}`, data),
  delete: (id) => api.delete(`/tips/${id}`),
};

export const storiesAPI = {
  getAll: (params) => api.get('/stories', { params }),
  create: (data) => api.post('/stories', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/stories/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/stories/${id}`),
};

export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getStats: () => api.get('/users/stats'),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const publicAPI = {
  subscribe: (email) => api.post('/public/newsletter', { email }),
  contact: (data) => api.post('/public/contact', data),
  getMessages: () => api.get('/public/contact'),
  deleteMessage: (id) => api.delete(`/public/contact/${id}`),
};

export const pageContentAPI = {
  getAll: (params) => api.get('/page-content', { params }),
  getOne: (id) => api.get(`/page-content/${id}`),
  create: (data) => api.post('/page-content', data),
  update: (id, data) => api.put(`/page-content/${id}`, data),
  delete: (id) => api.delete(`/page-content/${id}`),
};

export const landingAnimationAPI = {
  getAll: (params) => api.get('/landing-animations', { params }),
  create: (data) => api.post('/landing-animations', data),
  update: (id, data) => api.put(`/landing-animations/${id}`, data),
  delete: (id) => api.delete(`/landing-animations/${id}`),
};
